// @ts-check
/* eslint-disable jsdoc/require-description, jsdoc/require-param, jsdoc/require-tags */

/** @file Renders paginated task timelogs inside the board task detail modal. */

import _ from "gettext-universal/build/src/translate.js"
import {memo, useEffect, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {ScrollView, Text, View} from "react-native"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"

import Button from "../button"
import Pagination from "../pagination"
import {Timelog as RemoteTimelog} from "../../frontend-models/timelog"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import MemoizedTimelogRow from "./timelog-row"
import LocalTimelog from "../../models/timelog"
import LocalUser from "../../models/user"
import {projectReplicaCountSource, projectReplicaRowsSource} from "../../database/project-replica-reads"
import {queueOfflineTimelogDelete} from "../../database/project-replica-timelog-commands"
import {useActiveProjectReplica} from "../../sync/project-sync-lifecycle"
import useCurrentUser from "../../auth/use-current-user"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {OpenProjectReplica} from "../../database/project-replica.js" */
/** @import {default as Task} from "../../frontend-models/task" */

const PAGE_SIZE = 30
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskModalTimelogsProps
 * @property {boolean} [online] Whether online-only timelog commands are available.
 * @property {(timelog: import("../../frontend-models/timelog").default | null) => void} onEditTimelog Press handler for add and edit actions. Null opens a new timelog form.
 * @property {string} projectId Owning project replica id.
 * @property {import("../../database/project-replica.js").OpenProjectReplica | null} [replica] Open project replica for offline timelog deletion.
 * @property {{id: () => string | number}} task Task whose timelogs are shown.
 * @property {string} testID Base test id.
 */

/** @typedef {{page: number}} TaskModalTimelogsState */

/** Renders a paginated, task-scoped timelog list. */
class TaskModalTimelogs extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {ReturnType<typeof useCurrentUser>} */
  currentUser = this.hookField()
  /** @type {TaskModalTimelogsState} */
  state = {page: 1}
  loading = true
  status = /** @type {string | null} */ (null)
  /** @type {TaskTimelogReadView[]} */
  timelogs = []
  totalCount = 0

  /** Connects the selected task to tenant-local rows and count sources. */
  setup() {
    this.colors = useThemeColors()
    this.currentUser = useCurrentUser()
    this.themeName = useThemeName()
    const projectId = this.p.projectId
    const taskID = String(this.p.task.id())
    const page = this.s.page
    const replica = useActiveProjectReplica(projectId)
    const timelogsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTimelog,
      projectId,
      query: (timelogs) => timelogs.ransack({s: "date desc, id desc", taskIdEq: taskID}).offset((page - 1) * PAGE_SIZE).limit(PAGE_SIZE),
      replica
    }), [page, projectId, replica, taskID])
    const countSource = useMemo(() => replica && projectReplicaCountSource({
      modelClass: LocalTimelog,
      projectId,
      query: (timelogs) => timelogs.where({taskId: taskID}),
      replica
    }), [projectId, replica, taskID])
    const timelogsState = useLiveQuery(timelogsSource)
    const countState = useLiveQuery(countSource)
    const userIdsKey = [...new Set(timelogsState.results.map((timelog) => timelog.userId()).filter(Boolean))].sort().join(",")
    const usersQuery = useMemo(() => userIdsKey ? LocalUser.where({id: userIdsKey.split(",")}) : LocalUser.where({id: []}), [userIdsKey])
    const usersState = useLiveQuery(usersQuery)
    const usersById = Object.fromEntries(usersState.results.map((user) => [user.id(), user]))
    const queryStates = [timelogsState, countState, usersState]
    const totalCount = countState.results[0] || 0
    const loading = !replica || queryStates.some((state) => state.loading)

    useEffect(() => {
      if (loading) return
      const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
      if (page > totalPages) this.s.page = totalPages
    }, [loading, page, totalCount])

    this.timelogs = timelogsState.results.map((timelog) => new TaskTimelogReadView({
      timelog,
      user: usersById[String(timelog.userId())] || null
    }))
    this.totalCount = totalCount
    this.loading = loading
    this.status = queryStates.find((state) => state.error)?.error?.message || null
  }

  /** @returns {ReactNode} Timelog tab content. */
  render() {
    const {page} = this.s
    const {loading, status, timelogs, totalCount} = this
    const {testID} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <View style={styles.container ||= {flexShrink: 1, minHeight: 0}} testID={`${testID}/timelogs`}>
        <View style={styles.header ||= {alignItems: "center", flexDirection: "row", justifyContent: "space-between", marginBottom: 16}} testID={`${testID}/timelogs/header`}>
          <Text style={styles.title ||= {color: colors.text, fontSize: 18, fontWeight: 700}} testID={`${testID}/timelogs/title`}>{_("Time logs")}</Text>
          {this.tt.canCreateTimelog() &&
            <Button iconName="plus" label={_("New timelog")} onPress={this.tt.onNewTimelogPress} testID={`${testID}/timelogs/newButton`} />
          }
        </View>
        <ScrollView contentContainerStyle={styles.scrollContent ||= {paddingBottom: 4}} style={styles.scroll ||= {flexShrink: 1}} testID={`${testID}/timelogs/scroll`}>
          {status && <Text style={styles[`error-${themeName}`] ||= {color: colors.dangerText, fontWeight: 600, marginBottom: 12}} testID={`${testID}/timelogs/status`}>{status}</Text>}
          {loading && timelogs.length === 0 && <Text style={styles[`loading-${themeName}`] ||= {color: colors.textMuted}} testID={`${testID}/timelogs/loading`}>{_("Loading...")}</Text>}
          {!loading && !status && timelogs.length === 0 && <Text style={styles[`empty-${themeName}`] ||= {color: colors.textMuted}} testID={`${testID}/timelogs/empty`}>{_("No timelogs found")}</Text>}
          {timelogs.map((timelog) => {
            const canManage = this.tt.canManageTimelog(timelog)

            return <MemoizedTimelogRow canDelete={canManage} canEdit={canManage} key={timelog.id()} onDeleted={this.tt.onTimelogDeleted} onDeletePress={this.tt.onDeleteTimelog} onEditPress={this.tt.onEditTimelog} testID={`${testID}/timelogs/row-${timelog.id()}`} timelog={timelog} />
          })}
          {!loading && <Pagination disabled={loading} onPageChange={this.tt.onPageChange} page={page} pageSize={PAGE_SIZE} testID={`${testID}/timelogs/pagination`} totalCount={totalCount} />}
        </ScrollView>
      </View>
    )
  }

  /** @param {number} page Newly selected page. */
  onPageChange = (page) => { if (page !== this.s.page) this.s.page = page }

  /**
   * @param {TaskTimelogReadView} timelog Local timelog row.
   * @returns {boolean} Whether the signed-in user may mutate it online.
   */
  canManageTimelog(timelog) {
    const user = this.tt.currentUser.user

    return Boolean(
      (this.p.online || this.p.replica) &&
      user &&
      !timelog.invoiced() &&
      (user.admin || String(user.id) === String(timelog.userId()))
    )
  }

  /**
   * Reports whether the signed-in user may create a timelog for this task.
   * @returns {boolean} Online always allows the button; offline it requires an open replica
   * and the same ownership the server grants (admin or task owner).
   */
  canCreateTimelog() {
    if (this.p.online) return true
    if (!this.p.replica) return false
    const user = this.tt.currentUser.user
    return Boolean(user && (user.admin || String(user.id) === String(this.p.task.userId())))
  }

  /** Opens the new timelog modal. */
  onNewTimelogPress = () => { this.p.onEditTimelog(null) }
  /** Lets the local count source correct an empty final page after sync. */
  onTimelogDeleted = async () => {
    const page = Math.min(this.s.page, Math.max(1, Math.ceil(this.totalCount / PAGE_SIZE)))
    if (page !== this.s.page) { this.s.page = page; return }
  }

  /** Opens a remote command instance for one local timelog row. */
  onEditTimelog = (timelog) => { this.p.onEditTimelog(remoteTimelogForCommand(timelog)) }

  /**
   * Deletes one timelog: offline when a replica is open, otherwise through a
   * durable-id remote command. Invoiced rows are refused by the offline layer.
   */
  onDeleteTimelog = async (timelog) => {
    if (!this.p.online && this.p.replica) {
      await queueOfflineTimelogDelete({replica: this.p.replica, timelogId: timelog.id()})
      return
    }
    await remoteTimelogForCommand(timelog).destroy()
  }
}

/** @type {NamedExoticComponent<TaskModalTimelogsProps>} */
export default memo(shapeComponent(TaskModalTimelogs))

/** Immutable local timelog plus its catalog user. */
class TaskTimelogReadView {
  /**
   * Captures synchronized display records without adding command behavior.
   * @param {{timelog: InstanceType<typeof LocalTimelog>, user: InstanceType<typeof LocalUser> | null}} args Local records.
   */
  constructor({timelog, user}) {
    this.timelogRecord = timelog
    this.userRecord = user
  }

  /** @returns {string | null} Optional display comment. */
  comment() { return this.timelogRecord.comment() }

  /** @returns {Date | null} Work date used for ordering and display. */
  date() { return this.timelogRecord.date() }

  /** @returns {string} Durable timelog identifier. */
  id() { return this.timelogRecord.id() }

  /** @returns {boolean} Whether billing has locked this row. */
  invoiced() { return this.timelogRecord.invoiced() }

  /** @returns {number | null} Logged work duration in seconds. */
  timeSeconds() { return this.timelogRecord.timeSeconds() }

  /** @returns {string | null} Durable owner identifier used for command authorization. */
  userId() { return this.timelogRecord.userId() }

  /** @returns {{name: () => string | null} | null} Catalog user display contract when available. */
  user() {
    if (!this.userRecord) return null
    const name = [this.userRecord.firstName(), this.userRecord.lastName()].filter(Boolean).join(" ")
    return {name: () => name || null}
  }
}

/**
 * Builds an online command instance from an immutable local timelog row.
 * @param {TaskTimelogReadView} timelogView Local presentation view.
 * @returns {RemoteTimelog} Remote command instance.
 */
function remoteTimelogForCommand(timelogView) {
  const localTimelog = timelogView.timelogRecord

  return RemoteTimelog.instantiateFromResponse({
    comment: localTimelog.comment(),
    date: localTimelog.date(),
    id: localTimelog.id(),
    invoiced: localTimelog.invoiced(),
    taskId: localTimelog.taskId(),
    timeSeconds: localTimelog.timeSeconds(),
    timeTransportSeconds: localTimelog.timeTransportSeconds(),
    transportLength: localTimelog.transportLength(),
    userId: localTimelog.userId()
  })
}
